/**
 * Currency utilities for Yastık Medya site.
 *
 * Base currency for stored prices is TRY (Turkish Lira).
 * Manual fixed rates — Phase 3.5 (admin-editable in Phase 12).
 */

export const SUPPORTED_CURRENCIES = ["TRY", "USD", "EUR"];

export const CURRENCY_META = {
  TRY: { symbol: "₺", code: "TRY", label: "Türk Lirası", locale: "tr-TR" },
  USD: { symbol: "$", code: "USD", label: "US Dollar", locale: "en-US" },
  EUR: { symbol: "€", code: "EUR", label: "Euro", locale: "de-DE" },
};

/** Fixed rates: 1 TRY → X. Update in admin Settings (Phase 12). */
export const RATES_FROM_TRY = {
  TRY: 1,
  USD: 0.030,
  EUR: 0.028,
};

/** Default currency mapping by interface language. */
const LANG_DEFAULT = {
  tr: "TRY",
  ar: "USD",
  en: "USD",
  es: "EUR",
  de: "EUR",
  fr: "EUR",
};

export function defaultCurrencyForLang(lang) {
  return LANG_DEFAULT[lang] || "USD";
}

/**
 * Convert a TRY price into the target currency.
 * @param {number} amountTry  — base amount in TRY
 * @param {string} target     — TRY | USD | EUR
 * @param {object} rates      — optional override rates (default fallback)
 */
export function convertFromTry(amountTry, target, rates = RATES_FROM_TRY) {
  const rate = (rates && rates[target]) ?? RATES_FROM_TRY[target] ?? 1;
  return amountTry * rate;
}

/**
 * Round price for human-friendly display:
 *   ≥ 1000 → round to nearest 50
 *   ≥ 100  → round to nearest 5
 *   else   → 1 decimal
 */
function smartRound(value, currency) {
  if (currency === "TRY") {
    if (value >= 1000) return Math.round(value / 50) * 50;
    return Math.round(value);
  }
  // For USD/EUR amounts (after conversion they're smaller)
  if (value >= 100) return Math.round(value / 5) * 5;
  if (value >= 10) return Math.round(value);
  return Math.round(value * 10) / 10;
}

/**
 * Format an amount stored in TRY into a localized display string in target currency.
 *   formatPrice(6500, "USD") -> "$195"
 *   formatPrice(6500, "TRY") -> "₺6.500"
 */
export function formatPrice(amountTry, target = "TRY", rates = RATES_FROM_TRY) {
  if (amountTry === null || amountTry === undefined) return "";
  const meta = CURRENCY_META[target] || CURRENCY_META.TRY;
  const converted = convertFromTry(amountTry, target, rates);
  const rounded = smartRound(converted, target);
  try {
    const formatted = new Intl.NumberFormat(meta.locale, {
      maximumFractionDigits: rounded < 10 ? 1 : 0,
      minimumFractionDigits: 0,
    }).format(rounded);
    return `${meta.symbol}${formatted}`;
  } catch {
    return `${meta.symbol}${rounded}`;
  }
}
